import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from './services/firebase';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import FinishSignInPage from './pages/FinishSignInPage';
import OnboardingPage from './pages/OnboardingPage';
import ArtChatPage from './pages/ArtChatPage';
import CirclesPage from './pages/CirclesPage';
import CircleDetailPage from './pages/CircleDetailPage';
import CreateJoinCirclePage from './pages/CreateJoinCirclePage';
import JoinByLinkPage from './pages/JoinByLinkPage';
import PricingPage from './pages/PricingPage';
import BulkCirclesPage from './pages/BulkCirclesPage';
import MessageGeneratorPage from './pages/MessageGeneratorPage';
import SessionExpiredModal from './components/SessionExpiredModal';
import UpdateBanner from './components/UpdateBanner';
import { useVersionCheck } from './hooks/useVersionCheck';

function AppRoutes() {
  const { currentUser, loading, sessionExpired } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  const [hasOnboarded, setHasOnboarded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [minSplashDone, setMinSplashDone] = useState(false);
  const { hasUpdate, dismissUpdate } = useVersionCheck();

  const handleRefresh = () => {
    window.location.reload();
  };

  useEffect(() => {
    const t = setTimeout(() => setMinSplashDone(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (minSplashDone && !loading && (onboardingLoaded || !currentUser)) {
      const htmlSplash = document.getElementById('app-splash');
      if (htmlSplash) {
        htmlSplash.style.opacity = '0';
        htmlSplash.style.transition = 'opacity 0.3s';
        setTimeout(() => htmlSplash.remove(), 300);
      }
    }
  }, [minSplashDone, loading, onboardingLoaded, currentUser]);

  useEffect(() => {
    const handleAuthExpired = () => {
      setShowAuthModal(true);
    };
    
    window.addEventListener('artpingpong-auth-expired', handleAuthExpired);
    return () => window.removeEventListener('artpingpong-auth-expired', handleAuthExpired);
  }, []);
  
  useEffect(() => {
    if (sessionExpired) {
      setShowAuthModal(true);
    } else {
      setShowAuthModal(false);
    }
  }, [sessionExpired]);

  // Check if user has completed onboarding (stored in localStorage for anonymous users)
  useEffect(() => {
    const localOnboarding = localStorage.getItem('app_onboarding_completed');
    if (localOnboarding === 'true') {
      setHasOnboarded(true);
      setOnboardingLoaded(true);
      return;
    }

    if (!currentUser?.uid) {
      setOnboardingLoaded(true);
      setHasOnboarded(false);
      setShowOnboarding(true); // Show onboarding for unauthenticated users
      return;
    }
    
    // For authenticated users, check Firestore
    return onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const circleIds = data.circleIds || [];
        const legacyCircle = data.circleId;
        setHasOnboarded(!!data.onboardingCompleted || circleIds.length > 0 || !!legacyCircle);
      }
      setOnboardingLoaded(true);
    }, () => {
      setOnboardingLoaded(true);
    });
  }, [currentUser?.uid]);

  const isFinishSignInRoute = window.location.pathname === '/finishSignIn';
  const isJoinRoute = window.location.pathname.startsWith('/join/');
  
  const isReady = !loading && (onboardingLoaded || !currentUser) && minSplashDone;
  
  if (!isReady) return null;
  
  // Show onboarding FIRST for unauthenticated users (unless on special routes)
  if (!currentUser && !sessionExpired && !showAuthModal && !isFinishSignInRoute && !isJoinRoute && showOnboarding) {
    return <OnboardingPage onComplete={() => setShowOnboarding(false)} />;
  }
  
  // Then show login page after onboarding (with onboarding message if they just completed it)
  if (!currentUser && !sessionExpired && !showAuthModal && !isFinishSignInRoute && !isJoinRoute) {
    const justCompletedOnboarding = localStorage.getItem('app_onboarding_completed') === 'true';
    return <LoginPage showOnboardingMessage={justCompletedOnboarding} />;
  }

  // For authenticated users who haven't onboarded yet (legacy flow)
  if (!sessionExpired && !showAuthModal && currentUser && !hasOnboarded && !isFinishSignInRoute) {
    return <OnboardingPage onComplete={async () => {
      if (currentUser?.uid) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            onboardingCompleted: true,
          });
        } catch (e) {
          console.error('Failed to save onboarding state:', e);
        }
      }
      window.location.reload();
    }} />;
  }

  return (
    <SubscriptionProvider>
      {hasUpdate && <UpdateBanner onRefresh={handleRefresh} onDismiss={dismissUpdate} />}
      {showAuthModal && <SessionExpiredModal />}
      <Routes>
        <Route path="/finishSignIn" element={<FinishSignInPage />} />
        <Route path="/join/:code" element={<JoinByLinkPage />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/art" replace />} />
          <Route path="/art" element={<ArtChatPage />} />
          <Route path="/circles" element={<CirclesPage />} />
          <Route path="/circles/new" element={<CreateJoinCirclePage />} />
          <Route path="/circles/:id" element={<CircleDetailPage />} />
          <Route path="/admin/bulk-circles" element={<BulkCirclesPage />} />
          <Route path="/admin/messages" element={<MessageGeneratorPage />} />
          <Route path="/pricing" element={<PricingPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/art" replace />} />
      </Routes>
    </SubscriptionProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
